import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Plus, MapPin } from 'lucide-react';
import { getDeliveryAddresses, DeliveryAddress } from '@/services/partnerApi';
import { AddDeliveryAddressForm } from '@/components/account/AddDeliveryAddressForm';

interface DeliveryAddressSelectionProps {
  selectedAddressId: number | null;
  onAddressSelected: (addressId: number | null) => void;
  deliveryMethod: string;
}

export function DeliveryAddressSelection({ 
  selectedAddressId, 
  onAddressSelected, 
  deliveryMethod 
}: DeliveryAddressSelectionProps) {
  const { token } = useAuth();
  const { toast } = useToast();
  const [addresses, setAddresses] = useState<DeliveryAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const loadAddresses = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await getDeliveryAddresses(token);
      setAddresses(response.items);
    } catch (error) {
      console.error('Error loading delivery addresses:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les adresses de livraison.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, [token]);

  const handleAddressAdded = () => {
    setAddModalOpen(false);
    loadAddresses(); // Reload addresses
  };

  const handleAddressSelection = (value: string) => {
    if (value === 'none') {
      onAddressSelected(null);
    } else {
      onAddressSelected(parseInt(value));
    }
  };

  // Only show if delivery method is free_delivery
  if (deliveryMethod !== 'free_delivery') {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Adresse de livraison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Adresse de livraison
        </CardTitle>
        <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle adresse
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl w-[95vw]">
            <DialogHeader>
              <DialogTitle>Ajouter une adresse de livraison</DialogTitle>
            </DialogHeader>
            <AddDeliveryAddressForm onSuccess={handleAddressAdded} />
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {addresses.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              Aucune adresse de livraison trouvée.
            </p>
            <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter votre première adresse
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl w-[95vw]">
                <DialogHeader>
                  <DialogTitle>Ajouter une adresse de livraison</DialogTitle>
                </DialogHeader>
                <AddDeliveryAddressForm onSuccess={handleAddressAdded} />
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <RadioGroup 
            value={selectedAddressId?.toString() || 'none'} 
            onValueChange={handleAddressSelection}
          >
            <div className="space-y-4">
              {addresses.map((address) => (
                <div key={address.id} className="relative">
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem 
                      value={address.id.toString()} 
                      id={`address-${address.id}`}
                      className="mt-1"
                    />
                    <Label 
                      htmlFor={`address-${address.id}`} 
                      className="flex-1 cursor-pointer"
                    >
                      <Card className="transition-colors hover:bg-muted/50">
                        <CardContent className="p-4">
                          <div className="space-y-1">
                            <h4 className="font-medium">{address.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {address.street}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {address.city}, {address.state_name}, {address.zip_code}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {address.country_name}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </Label>
                  </div>
                </div>
              ))}
            </div>
          </RadioGroup>
        )}
        
        {addresses.length > 0 && (
          <p className="text-xs text-muted-foreground mt-4">
            Sélectionnez une adresse de livraison pour votre commande.
          </p>
        )}
      </CardContent>
    </Card>
  );
}